import React, { useEffect, useState } from "react";
import {
  Menu,
  X,
  LogIn,
  LogOut,
  CheckCircle2,
  Mail,
  ArrowRight,
  Home,
  Layout,
  Activity,
} from "lucide-react";
import HomeView from "./views/HomeView";
import PlanView from "./views/PlanView";
import ResultsView from "./views/ResultsView";
import ClientView from "./views/ClientView";
import WhyThisWorksModal from "./components/WhyThisWorksModal";
import HeaderLogo from "./components/HeaderLogo";
import "./styles.css";

/* --- INTERNAL COMPONENT: CONTACT MODAL --- */
const ContactModal = ({ open, onClose }) => {
  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgba(0,0,0,0.85)",
        backdropFilter: "blur(8px)",
        padding: "20px",
        animation: "fadeIn 0.2s ease-out",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: "480px",
          background: "#0b0c10",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: "24px",
          padding: "40px",
          textAlign: "center",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
          position: "relative",
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "16px",
            right: "16px",
            background: "transparent",
            border: "none",
            color: "var(--muted)",
            cursor: "pointer",
          }}
        >
          <X size={20} />
        </button>

        <div
          style={{
            width: "64px",
            height: "64px",
            background: "rgba(34,197,94,0.1)",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 24px",
          }}
        >
          <Mail size={32} color="var(--brand)" />
        </div>

        <h3
          style={{
            fontSize: "24px",
            color: "#fff",
            marginBottom: "12px",
            fontWeight: "800",
            letterSpacing: "-0.02em",
          }}
        >
          Ready to build?
        </h3>

        <p
          style={{
            color: "var(--muted)",
            fontSize: "15px",
            lineHeight: "1.6",
            marginBottom: "32px",
          }}
        >
          We screen every client to ensure the system is a match. Send us a
          secure message to start the intake process.
        </p>

        <a
          href="mailto:scott@simplerhealth.com?subject=Simpler%20Health%20Intake&body=I%27m%20ready%20to%20build%20the%20house."
          className="btn primary"
          style={{
            width: "100%",
            justifyContent: "center",
            textDecoration: "none",
            fontSize: "16px",
            padding: "16px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            borderRadius: "12px",
          }}
        >
          Email Scott Directly <ArrowRight size={18} />
        </a>

        <div
          style={{
            marginTop: "24px",
            fontSize: "12px",
            color: "var(--muted)",
            opacity: 0.7,
          }}
        >
          Response time: &lt; 2 hours
        </div>
      </div>
    </div>
  );
};

/* ------- App Shell ------- */
export default function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [activeTab, setActiveTab] = useState("home");

  // Modals
  const [whyOpen, setWhyOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setWhyOpen((x) => !x);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const render = () => {
    if (isLoggedIn) return <ClientView />;
    if (activeTab === "plan")
      return <PlanView onOpenBooking={() => setBookingOpen(true)} />;
    if (activeTab === "results") return <ResultsView />;

    return (
      <HomeView
        onNavigate={setActiveTab}
        onOpenWhy={() => setWhyOpen(true)}
        onOpenBooking={() => setBookingOpen(true)}
      />
    );
  };

  return (
    <div className="app">
      {/* NAV */}
      <nav
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          background: "rgba(11, 12, 16, 0.85)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid var(--line)",
          height: "72px",
        }}
      >
        <div
          className="container"
          style={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* LOGO AREA */}
          <div
            onClick={() => {
              setActiveTab("home");
              setIsLoggedIn(false);
            }}
            style={{
              cursor: "pointer",
              userSelect: "none",
              display: "flex",
              alignItems: "center",
            }}
          >
            <HeaderLogo />
          </div>

          {/* DESKTOP LINKS */}
          <div
            className="desktop-links"
            style={{ display: "flex", alignItems: "center", gap: 32 }}
          >
            {!isLoggedIn &&
              [
                { id: "home", label: "Start Here" },
                { id: "plan", label: "Build Sequence" },
                { id: "results", label: "Proof" },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  style={{
                    textTransform: "uppercase",
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: "0.05em",
                    color: activeTab === item.id ? "#fff" : "var(--muted)",
                    transition: "color 0.2s",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  {activeTab === item.id && (
                    <span
                      style={{
                        width: 4,
                        height: 4,
                        borderRadius: "50%",
                        background: "var(--brand)",
                      }}
                    ></span>
                  )}
                  {item.label}
                </button>
              ))}

            <div style={{ width: 1, height: 20, background: "var(--line)" }} />

            <button
              onClick={() => setIsLoggedIn(!isLoggedIn)}
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: isLoggedIn ? "var(--brand)" : "#fff",
                display: "flex",
                alignItems: "center",
                gap: 8,
                cursor: "pointer",
              }}
            >
              {isLoggedIn ? <LogOut size={16} /> : <LogIn size={16} />}
              {isLoggedIn ? "Log Out" : "Client Portal"}
            </button>

            {!isLoggedIn && (
              <button
                className="btn primary"
                onClick={() => setBookingOpen(true)}
                style={{ fontSize: 12, padding: "8px 16px" }}
              >
                Book Now
              </button>
            )}
          </div>

          {/* MOBILE TOGGLE */}
          <div className="mobile-toggle">
            <button
              style={{ color: "#fff", background: "none", border: "none" }}
              onClick={() => setMenuOpen(true)}
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </nav>

      {/* MOBILE MENU OVERLAY (ADDED THIS) */}
      {menuOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 100,
            background: "#0b0c10",
            padding: "24px",
            animation: "fadeIn 0.2s ease-out",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "40px",
            }}
          >
            <HeaderLogo />
            <button
              onClick={() => setMenuOpen(false)}
              style={{ color: "#fff", background: "none", border: "none" }}
            >
              <X size={24} />
            </button>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "24px",
              fontSize: "20px",
              fontWeight: "700",
            }}
          >
            {[
              { id: "home", label: "Start Here", icon: <Home /> },
              { id: "plan", label: "Build Sequence", icon: <Layout /> },
              { id: "results", label: "Proof", icon: <Activity /> },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  setActiveTab(item.id);
                  setMenuOpen(false);
                  setIsLoggedIn(false);
                }}
                style={{
                  color: activeTab === item.id ? "#22c55e" : "#fff",
                  background: "none",
                  border: "none",
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                }}
              >
                {item.icon} {item.label}
              </button>
            ))}

            <div
              style={{
                height: "1px",
                background: "rgba(255,255,255,0.1)",
                margin: "16px 0",
              }}
            />

            <button
              onClick={() => {
                setIsLoggedIn(!isLoggedIn);
                setMenuOpen(false);
              }}
              style={{
                color: isLoggedIn ? "#22c55e" : "#fff",
                background: "none",
                border: "none",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                gap: "16px",
              }}
            >
              {isLoggedIn ? <LogOut /> : <LogIn />}
              {isLoggedIn ? "Log Out" : "Client Portal"}
            </button>

            <button
              onClick={() => {
                setBookingOpen(true);
                setMenuOpen(false);
              }}
              className="btn primary"
              style={{ justifyContent: "center", marginTop: "16px" }}
            >
              Book Now
            </button>
          </div>
        </div>
      )}

      {/* Style Logic for Mobile/Desktop */}
      <style>{`
        @media (min-width: 768px) {
          .mobile-toggle { display: none; }
          .desktop-links { display: flex; }
        }
        @media (max-width: 767px) {
          .mobile-toggle { display: block; }
          .desktop-links { display: none !important; }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>

      {/* MAIN */}
      <main style={{ paddingTop: "100px", minHeight: "100vh" }}>
        {render()}
      </main>

      {/* FOOTER */}
      <footer
        style={{
          borderTop: "1px solid var(--line)",
          padding: "60px 0",
          marginTop: "60px",
        }}
      >
        <div
          className="container"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 20,
          }}
        >
          <div
            style={{
              fontFamily: "monospace",
              fontSize: 12,
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "var(--muted)",
            }}
          >
            <div
              style={{
                width: 6,
                height: 6,
                background: "var(--brand)",
                borderRadius: "50%",
              }}
            />
            SYSTEM STATUS:{" "}
            <span style={{ color: "var(--brand)" }}>OPTIMAL</span>
          </div>
          <div className="muted" style={{ fontSize: 13 }}>
            © {new Date().getFullYear()} Simpler Health. Evidence first.
          </div>
        </div>
      </footer>

      {/* --- MODALS --- */}

      {/* 1. OPERATOR LENS (The "Why") */}
      <WhyThisWorksModal
        open={whyOpen}
        onClose={() => setWhyOpen(false)}
        onBook={() => setBookingOpen(true)}
      />

      {/* 2. DIRECT CONTACT MODAL (Replaces Calendly) */}
      <ContactModal open={bookingOpen} onClose={() => setBookingOpen(false)} />
    </div>
  );
}
